import { useState, useEffect, useCallback } from "react";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useTheme } from "../../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useMoodleV2 } from "../../../contexts/moodle";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const DIAS_SEMANA = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/**
 * Convierte un timestamp Unix (segundos) a string "YYYY-MM-DD"
 */
const unixToIso = (ts) => {
  const d = new Date(ts * 1000);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplayDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${day} ${MESES[month - 1]} ${year}`;
};

const getDaysUntil = (isoDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + "T00:00:00");
  const diff = Math.ceil((target - today) / 86400000);
  if (diff < 0) return { label: "Vencido", color: "#ef4444" };
  if (diff === 0) return { label: "Hoy", color: "#f59e0b" };
  if (diff === 1) return { label: "Mañana", color: "#f59e0b" };
  if (diff <= 3) return { label: `En ${diff} días`, color: "#f59e0b" };
  return { label: `En ${diff} días`, color: "#22c55e" };
};

/**
 * Determina el tipo legible del evento según el modname de Moodle.
 * core_calendar_get_action_events_by_timesort devuelve objetos con:
 *   id, name, description, modulename, activityname, course { id, fullname },
 *   timesort (Unix timestamp del vencimiento), etc.
 */
export const resolveEventType = (modulename = "") => {
  const mod = modulename.toLowerCase();
  if (["quiz", "assign", "workshop", "scorm", "lesson", "h5pactivity", "url"].includes(mod)) return "Actividad";
  return "Evento";
};

/**
 * Normaliza la respuesta de core_calendar_get_action_events_by_timesort
 * al formato interno del componente.
 */
const normalizeEvents = (moodleEvents = []) =>
  moodleEvents.map((ev) => {
    const d = new Date(ev.timesort * 1000);
    const horas = String(d.getHours()).padStart(2, "0");
    const minutos = String(d.getMinutes()).padStart(2, "0");
    return {
      id: ev.coursemoduleid || ev.instance, // Prefer coursemoduleid for navigation
      titulo: ev.name,
      curso: ev.course?.fullname ?? ev.activityname ?? "",
      courseid: ev.course?.id ?? "",
      vencimiento: unixToIso(ev.timesort),
      hora: `${horas}:${minutos}`,
      tipo: resolveEventType(ev.modulename),
      modulename: ev.modulename,
    };
  });

/* ── Main component ─────────────────────────────────────────────────────── */

export const ActividadesYEventos = () => {
  const navigation = useNavigate();
  const moodle = useMoodleV2();

  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(moodle.calendarEvents === null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("fechados");
  const [loadingUndated, setLoadingUndated] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };

  const isDark = mode === "dark";
  const bgMain = colors.backgroundColorBody || (isDark ? "#0f172a" : "#f8fafc");
  const bgCard = colors.backgroundColorDetailCourse || (isDark ? "#1e293b" : "#ffffff");
  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const accent = colors.button_light || "#fdbf00";

  // Eventos normalizados desde el contexto
  const actividades = normalizeEvents(moodle.calendarEvents ?? []);

  /* ── Fetch de datos ── */
  const fetchEvents = useCallback(async () => {
    if (!moodle.getCalendarEvents) return;
    setLoading(true);
    setError(null);
    try {
      await moodle.getCalendarEvents();
    } catch (err) {
      console.error("ActividadesYEventos:", err);
      setError("No se pudieron cargar los eventos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [moodle.getCalendarEvents]);

  useEffect(() => {
    if (moodle.calendarEvents === null) {
      fetchEvents();
    }
  }, [moodle.calendarEvents]);

  /* ── Fetch de módulos sin fecha ── */
  useEffect(() => {
    if (activeTab === "todas" && moodle.undatedModules === null && !loadingUndated && moodle.getUndatedModules) {
      setLoadingUndated(true);
      moodle.getUndatedModules().finally(() => setLoadingUndated(false));
    }
  }, [activeTab, moodle.undatedModules, loadingUndated, moodle.getUndatedModules]);

  const refreshUndated = useCallback(() => {
    setLoadingUndated(true);
    moodle.setUndatedModules(null);
    moodle.getUndatedModules().finally(() => setLoadingUndated(false));
  }, [moodle.setUndatedModules, moodle.getUndatedModules]);

  /* ── Datos derivados ── */
  const datesWithEvents = actividades.map((a) => a.vencimiento);
  const datesWithTypes = actividades.reduce((acc, a) => {
    if (!acc[a.vencimiento]) acc[a.vencimiento] = {};
    acc[a.vencimiento][a.tipo] = true;
    return acc;
  }, {});

  const filteredActividades = selectedDate ? actividades.filter((a) => a.vencimiento === selectedDate) : actividades;

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date && activeTab === "todas") {
      setActiveTab("fechados");
    }
  };

  const groupedModules =
    moodle.undatedModules?.reduce((acc, mod) => {
      if (!acc[mod.courseid]) {
        acc[mod.courseid] = { courseName: mod.curso, courseid: mod.courseid, modules: [] };
      }
      acc[mod.courseid].modules.push(mod);
      return acc;
    }, {}) ?? {};

  const groupedList = Object.values(groupedModules);
  const selectedCourse = selectedCourseId ? groupedModules[selectedCourseId] : null;

  return (
    <>
      {/* ── Scoped styles ── */}
      <style>{`
        .aye-layout {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .aye-list-panel {
          flex: 1 1 320px;
          min-width: 0;
        }
        .aye-calendar-panel {
          width: 100%;
          flex-shrink: 0;
        }
        @media (min-width: 768px) {
          .aye-calendar-panel {
            width: clamp(240px, 22vw, 300px);
          }
        }
        .aye-item:last-child {
          border-bottom: none !important;
        }
        .aye-item:hover {
          background-color: var(--aye-hover);
        }
        .aye-cal-btn {
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border-radius: 6px;
          transition: background 0.15s;
        }
        .aye-cal-btn:hover {
          background-color: var(--aye-hover);
        }
        .aye-day-btn {
          background: none;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          padding: 5px 0;
          font-size: 12px;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          position: relative;
        }
        .aye-day-btn:hover {
          background-color: var(--aye-hover);
        }
        .aye-fade-in {
          animation: ayeFadeIn 0.2s ease;
        }
        @keyframes ayeFadeIn {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div
        style={{
          backgroundColor: bgMain,
          padding: "clamp(16px, 3vw, 36px) clamp(14px, 3vw, 28px)",
          fontFamily: "'Inter','Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif",
          minHeight: "100%",
          "--aye-hover": isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
        }}
      >
        <div style={{ maxWidth: 1360, margin: "0 auto" }}>
          {/* ── Título ── */}
          {/* <h2
            style={{
              fontSize: "clamp(18px, 2.5vw, 26px)",
              fontWeight: 800,
              color: readableColor(colors.backgroundColorBody, colors.textColorNavbar),
              margin: "0 0 20px 0",
              letterSpacing: "-0.03em",
              position: "relative",
              display: "inline-block",
            }}
          >
            Actividades y Eventos
          </h2> */}
          <div className="aye-layout">
            {/* ── Panel izquierdo: listado ── */}
            <div className="aye-list-panel max-[767px]:order-2">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 14,
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Franja de color */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                  }}
                />

                {/* Header del panel */}
                <div
                  style={{
                    padding: "14px 20px",
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 18,
                      backgroundColor: accent,
                      borderRadius: 2,
                    }}
                  />
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    style={{
                      color: textPrimary,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      paddingRight: 18,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right center",
                      backgroundSize: 14,
                    }}
                    className="!text-[14px] font-extrabold"
                  >
                    <option value="fechados">{selectedDate ? `Actividades del ${formatDisplayDate(selectedDate)}` : "Próximas actividades"}</option>
                    <option value="todas">Todas las actividades</option>
                  </select>

                  {/* Botón recargar */}
                  <button
                    onClick={activeTab === "todas" ? refreshUndated : fetchEvents}
                    disabled={activeTab === "todas" ? loadingUndated : loading}
                    title="Recargar"
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      cursor: (activeTab === "todas" ? loadingUndated : loading) ? "default" : "pointer",
                      color: textSecondary,
                      display: "flex",
                      alignItems: "center",
                      padding: 4,
                      borderRadius: 6,
                      opacity: (activeTab === "todas" ? loadingUndated : loading) ? 0.4 : 1,
                    }}
                  >
                    <RefreshIcon style={{ fontSize: 16 }} />
                  </button>

                  {activeTab === "fechados" && selectedDate && (
                    <button
                      onClick={() => setSelectedDate(null)}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: accent,
                        background: "none",
                        border: `1px solid ${accent}44`,
                        borderRadius: 20,
                        padding: "2px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Ver todas
                    </button>
                  )}

                  {activeTab === "fechados" && !selectedDate && !loading && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: textSecondary,
                        backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                        borderRadius: 20,
                        padding: "2px 10px",
                      }}
                    >
                      {actividades.length} pendientes
                    </span>
                  )}

                  {activeTab === "todas" && moodle.undatedModules && !loadingUndated && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: textSecondary,
                        backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                        borderRadius: 20,
                        padding: "2px 10px",
                      }}
                    >
                      {moodle.undatedModules.length}
                    </span>
                  )}
                </div>

                {/* Estados: cargando / error / vacío / lista */}
                {activeTab === "fechados" ? (
                  loading ? (
                    <div
                      style={{
                        padding: "40px 20px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <CircularProgress size={28} style={{ color: accent }} />
                      <span style={{ fontSize: 13, color: textSecondary }}>Cargando actividades…</span>
                    </div>
                  ) : error ? (
                    <div style={{ padding: "32px 20px", textAlign: "center" }}>
                      <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 10 }}>{error}</p>
                      <button
                        onClick={fetchEvents}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: accent,
                          background: "none",
                          border: `1px solid ${accent}44`,
                          borderRadius: 20,
                          padding: "4px 14px",
                          cursor: "pointer",
                        }}
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : filteredActividades.length === 0 ? (
                    <div style={{ padding: "32px 20px", textAlign: "center" }}>
                      <CalendarMonthIcon style={{ fontSize: 36, color: textSecondary, opacity: 0.4 }} />
                      <p
                        style={{
                          fontSize: 13,
                          color: textSecondary,
                          marginTop: 10,
                          fontStyle: "italic",
                        }}
                      >
                        {selectedDate ? "Sin actividades para este día" : "No hay actividades próximas"}
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowY: "scroll", maxHeight: "370.5px" }}>
                      {filteredActividades.map((item) => {
                        const urgencia = getDaysUntil(item.vencimiento);
                        const isActividad = item.tipo === "Actividad";
                        const tipoColor = isActividad ? "#3b82f6" : "#8b5cf6";

                        return (
                          <div
                            key={item.id}
                            className="aye-item hover:cursor-pointer"
                            onClick={() => {
                              moodle.setCurrentModules(null);
                              moodle.setIframeLoaded(false);
                              navigation(`/detailcourse/${item.courseid}?resource=${item.id}`);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 14,
                              padding: "15px 20px",
                              borderBottom: `1px solid ${borderColor}`,
                              transition: "background-color 0.15s",
                            }}
                          >
                            {/* Icono tipo */}
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                backgroundColor: `${tipoColor}18`,
                                border: `1px solid ${tipoColor}30`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {isActividad ? (
                                <AssignmentIcon style={{ fontSize: 18, color: tipoColor }} />
                              ) : (
                                <EventIcon style={{ fontSize: 18, color: tipoColor }} />
                              )}
                            </div>

                            {/* Texto */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 3,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: textPrimary,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {item.titulo}
                                </span>
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: "2px 8px",
                                    borderRadius: 20,
                                    backgroundColor: `${tipoColor}18`,
                                    color: tipoColor,
                                    border: `1px solid ${tipoColor}30`,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.tipo}
                                </span>
                              </div>
                              {item.curso && (
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: textSecondary,
                                    fontWeight: 500,
                                  }}
                                >
                                  {item.curso}
                                </span>
                              )}
                            </div>

                            {/* Fecha vencimiento */}
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  justifyContent: "flex-end",
                                  marginBottom: 3,
                                }}
                              >
                                <AccessTimeIcon style={{ fontSize: 12, color: urgencia.color }} />
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: urgencia.color,
                                  }}
                                >
                                  {urgencia.label}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: textSecondary,
                                  fontWeight: 500,
                                }}
                              >
                                {formatDisplayDate(item.vencimiento)} a las {item.hora}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : loadingUndated ? (
                  <div
                    style={{
                      padding: "40px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <CircularProgress size={28} style={{ color: accent }} />
                    <span style={{ fontSize: 13, color: textSecondary }}>Cargando todas las actividades…</span>
                  </div>
                ) : !moodle.undatedModules || moodle.undatedModules.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <CalendarMonthIcon style={{ fontSize: 36, color: textSecondary, opacity: 0.4 }} />
                    <p
                      style={{
                        fontSize: 13,
                        color: textSecondary,
                        marginTop: 10,
                        fontStyle: "italic",
                      }}
                    >
                      No hay actividades disponibles
                    </p>
                  </div>
                ) : selectedCourse ? (
                  <div className="aye-fade-in" style={{ overflowY: "auto", maxHeight: "370.5px" }}>
                    {/* Back button + course header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 20px",
                        borderBottom: `1px solid ${borderColor}`,
                        backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f1f5f9",
                      }}
                    >
                      <button
                        onClick={() => setSelectedCourseId(null)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px 10px 4px 6px",
                          borderRadius: 8,
                          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
                          color: textPrimary,
                          fontSize: 12,
                          fontWeight: 700,
                          transition: "background-color 0.15s",
                        }}
                      >
                        <ArrowBackIcon style={{ fontSize: 15, color: accent }} />
                        Volver
                      </button>
                      {/* <div style={{ width: 4, height: 14, backgroundColor: accent, borderRadius: 2, flexShrink: 0 }} /> */}
                      <span style={{ fontSize: 13, fontWeight: 700, color: textPrimary }}>{selectedCourse.courseName}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: textSecondary,
                          backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                          borderRadius: 20,
                          padding: "2px 8px",
                        }}
                      >
                        {selectedCourse.modules.length}
                      </span>
                    </div>

                    {/* Module list */}
                    {selectedCourse.modules.map((item) => {
                      const isActividad = item.tipo === "Actividad";
                      const tipoColor = isActividad ? "#3b82f6" : "#8b5cf6";

                      return (
                        <div
                          key={item.id}
                          className="aye-item hover:cursor-pointer"
                          onClick={() => {
                            moodle.setCurrentModules(null);
                            moodle.setIframeLoaded(false);
                            navigation(`/detailcourse/${item.courseid}?resource=${item.id}`);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "15px 20px",
                            borderBottom: `1px solid ${borderColor}`,
                            transition: "background-color 0.15s",
                          }}
                        >
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 10,
                              backgroundColor: `${tipoColor}18`,
                              border: `1px solid ${tipoColor}30`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {isActividad ? (
                              <AssignmentIcon style={{ fontSize: 18, color: tipoColor }} />
                            ) : (
                              <EventIcon style={{ fontSize: 18, color: tipoColor }} />
                            )}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: textPrimary, lineHeight: 1.3 }}>{item.titulo}</span>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 20,
                                  backgroundColor: `${tipoColor}18`,
                                  color: tipoColor,
                                  border: `1px solid ${tipoColor}30`,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.tipo}
                              </span>
                            </div>
                          </div>

                          {/* <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: textSecondary,
                              backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                              borderRadius: 20,
                              padding: "2px 8px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            Sin fecha
                          </span> */}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="aye-fade-in" style={{ overflowY: "auto", maxHeight: "370.5px" }}>
                    {groupedList.map((group) => (
                      <div
                        key={group.courseid}
                        onClick={() => setSelectedCourseId(group.courseid)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "15px 20px",
                          cursor: "pointer",
                          borderBottom: `1px solid ${borderColor}`,
                          transition: "background-color 0.15s",
                        }}
                        className="aye-item"
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: `${accent}18`,
                            border: `1px solid ${accent}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontSize: 14, fontWeight: 800, color: accent }}>{group.courseName.charAt(0).toUpperCase()}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: textPrimary, display: "block", marginBottom: 2 }}>
                            {group.courseName}
                          </span>
                          <span style={{ fontSize: 12, color: textSecondary, fontWeight: 500 }}>
                            {group.modules.length} actividad{group.modules.length !== 1 ? "es" : ""}
                          </span>
                        </div>
                        <span style={{ fontSize: 18, color: textSecondary }}>›</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Panel derecho: calendario ── */}
            <div className="aye-calendar-panel max-[767px]:order-1">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 14,
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Franja de color */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                  }}
                />

                {/* Header del calendario */}
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div style={{ width: 4, height: 18, backgroundColor: accent, borderRadius: 2 }} />
                  <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>Calendario</span>
                </div>

                <div style={{ padding: "16px 18px" }}>
                  <MiniCalendar
                    selectedDate={selectedDate}
                    setSelectedDate={handleDateSelect}
                    datesWithEvents={datesWithEvents}
                    datesWithTypes={datesWithTypes}
                    accent={accent}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    borderColor={borderColor}
                    isDark={isDark}
                  />
                </div>

                {/* Leyenda */}
                <div
                  style={{
                    padding: "12px 18px",
                    borderTop: `1px solid ${borderColor}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {activeTab === "fechados" ? (
                    <>
                      <LegendItem color="#3b82f6" label="Actividad" />
                      <LegendItem color="#8b5cf6" label="Evento" />
                      <LegendItem color={accent} label="Fecha seleccionada" />
                    </>
                  ) : (
                    <>
                      <LegendItem color="#3b82f6" label="Actividad" />
                      <LegendItem color="#8b5cf6" label="Evento" />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Leyenda ─────────────────────────────────────────────────────────────── */

const LegendItem = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>{label}</span>
  </div>
);

/* ── Mini calendario ─────────────────────────────────────────────────────── */

function MiniCalendar({ selectedDate, setSelectedDate, datesWithEvents, datesWithTypes, accent, textPrimary, textSecondary, borderColor, isDark }) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Se ajusta para que el lunes sea el primer día de la semana (0) y el domingo el último (6)
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;

  const formatDate = (day) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  const todayStr = today.toISOString().split("T")[0];

  return (
    <div>
      {/* Navegación de mes */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <button className="aye-cal-btn" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={{ color: textSecondary }}>
          <ChevronLeftIcon style={{ fontSize: 18 }} />
        </button>

        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: textPrimary,
            textTransform: "capitalize",
          }}
        >
          {MESES[month]} {year}
        </span>

        <button className="aye-cal-btn" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={{ color: textSecondary }}>
          <ChevronRightIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Días de la semana */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
          marginBottom: 6,
        }}
      >
        {DIAS_SEMANA.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 10,
              fontWeight: 800,
              color: textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              padding: "2px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Días del mes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
        }}
      >
        {/* Celdas vacías */}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Días */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const fullDate = formatDate(day);
          const hasEvent = datesWithEvents.includes(fullDate);
          const isSelected = selectedDate === fullDate;
          const isToday = fullDate === todayStr;

          return (
            <button
              key={day}
              className="aye-day-btn"
              onClick={() => setSelectedDate(isSelected ? null : fullDate)}
              style={{
                color: isSelected ? "#fff" : isToday ? accent : textPrimary,
                backgroundColor: isSelected ? accent : "transparent",
                fontWeight: isToday || isSelected ? 800 : 500,
                border: isToday && !isSelected ? `1px solid ${accent}80` : "1px solid transparent",
              }}
            >
              {day}
              {/* Puntos indicadores de evento por tipo */}
              {hasEvent && (() => {
                const types = datesWithTypes[fullDate] ?? {};
                const typeCount = (types.Actividad ? 1 : 0) + (types.Evento ? 1 : 0);
                if (typeCount === 1) {
                  const color = types.Actividad ? "#3b82f6" : "#8b5cf6";
                  return (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 2,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        backgroundColor: color,
                        display: "block",
                      }}
                    />
                  );
                }
                return (
                  <>
                    {types.Actividad && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: 2,
                          left: "calc(50% - 6px)",
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          backgroundColor: "#3b82f6",
                          display: "block",
                        }}
                      />
                    )}
                    {types.Evento && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: 2,
                          left: "calc(50% + 2px)",
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          backgroundColor: "#8b5cf6",
                          display: "block",
                        }}
                      />
                    )}
                  </>
                );
              })()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
